import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Modal, Tabs } from 'antd';
import useProjectsRunState from '../hooks/useProjectsRunState';
import useWorkspaceRun from '../hooks/useWorkspaceRun';
import useWorkspaceQuality from '../hooks/useWorkspaceQuality';
import useWorkspaceActivity from '../hooks/useWorkspaceActivity';
import useWorkspaceSecrets from '../hooks/useWorkspaceSecrets';
import { blockedTotal } from '../utils/workspacePageModel';
import { useAuth } from '../context/AuthContext';
import {
  canEdit, canManage, deleteWorkspace, getWorkspace,
} from '../services/WorkspacesService';
import WorkspaceMembers from '../components/workspaces/WorkspaceMembers';
import WorkspaceBrain from '../components/workspaces/WorkspaceBrain';
import WorkspaceProjects from '../components/workspaces/WorkspaceProjects';
import WorkspaceHeader from '../components/workspaces/WorkspaceHeader';
import WorkspaceSettings from '../components/workspaces/WorkspaceSettings';
import RunConfirm from '../components/workspaces/RunConfirm';
import RenameWorkspace from '../components/workspaces/RenameWorkspace';
import WorkspaceOverview from '../components/workspaces/WorkspaceOverview';
import WorkspaceQuality from '../components/workspaces/WorkspaceQuality';
import WorkspaceActivity from '../components/workspaces/WorkspaceActivity';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';
import './Workspaces.css';
import './WorkspacePage.css';

const ROLE_TEXT = { owner: 'Owner', editor: 'Editor', viewer: 'Viewer', none: 'No access' };

const TABS = ['overview', 'projects', 'quality', 'activity', 'members', 'brain', 'settings'];

/**
 * One workspace (vfs-service docs/workspaces.md; design "Flow Workspace"): a header with Start all, Stop all, Invite and
 * "⋯" (Rename, Delete), and the tabs: its projects (each with the person's role on it; "Add project" for owners and
 * editors, who move their own projects in; "Remove" for the project's owner or the workspace's), its members
 * (components/workspaces/WorkspaceMembers.jsx), its brain (components/workspaces/WorkspaceBrain.jsx) and its settings.
 * The owner can delete it: its projects are kept. The tab is in the URL (`?tab=`).
 */
export default function Workspace() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [pending, setPending] = useState(null); // the Start all / Stop all waiting for the person's yes
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = TABS.includes(requested) ? requested : TABS[0];
  const setTab = (key) => setSearchParams(key === TABS[0] ? {} : { tab: key }, { replace: true });

  // Each project's run state, loaded a few at a time: what Start all and Stop all (and the tabs) read.
  const projectRun = useProjectsRunState();
  const run = useWorkspaceRun(projectRun);
  const projectIds = (workspace?.projects || []).map((project) => project.projectId).join('|');
  const { want } = projectRun;
  useEffect(() => { projectIds.split('|').filter(Boolean).forEach(want); }, [projectIds, want]);
  // Their quality gates (cheap) and activity, a few at a time; the activity only where a tab shows it.
  const ids = projectIds ? projectIds.split('|') : [];
  // The Quality tab reads every check of each project (heavier), the others only the gates.
  const { entries: quality, reload: reloadQuality } = useWorkspaceQuality(ids, { enabled: Boolean(workspace), full: tab === 'quality' });
  const { entries: secrets, reload: reloadSecrets } = useWorkspaceSecrets(ids, { enabled: Boolean(workspace) && tab === 'quality' });
  const { entries: activity, reload: reloadActivity } = useWorkspaceActivity(ids, { enabled: Boolean(workspace) && ['overview', 'projects', 'activity'].includes(tab) });
  // `/open/<project>?tab=<name>`: one `?tab=` for every project tab (Logs too).
  // `params`: the tab's element (`{ check: 'vulnerabilities' }`), passed on by /open (pages/OpenProject).
  const openProject = (projectId, { tab: projectTab, params = null } = {}) => {
    const query = new URLSearchParams();
    if (projectTab) { query.set('tab', projectTab); }
    Object.entries(params || {}).forEach(([key, value]) => { if (value) { query.set(key, String(value)); } });
    const text = query.toString();
    navigate(`/open/${encodeURIComponent(projectId)}${text ? `?${text}` : ''}`);
  };

  const load = useCallback(async() => {
    try {
      setWorkspace(await getWorkspace(workspaceId));
      setError(null);
    } catch (failed) {
      setError(failed);
    }
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const remove = async() => {
    try {
      await deleteWorkspace(workspaceId);
      navigate('/workspaces');
    } catch (failed) {
      setActionError(failed.message);
      setConfirmDelete(false);
    }
  };

  if (error) {
    return (
      <div className="sbx-home"><div className="sbx-home-inner workspaces is-full-width" data-testid="workspace-page-main">
        <p className="sbx-action-error" role="alert">{error.status === 404 ? 'This workspace does not exist, or you are not in it.' : error.message}</p>
        <Button onClick={() => navigate('/workspaces')}>All workspaces</Button>
      </div></div>
    );
  }
  if (!workspace) {
    return <div className="sbx-home"><div className="sbx-home-inner workspaces is-full-width"><p className="workspaces-note">Loading the workspace…</p></div></div>;
  }

  const blocked = blockedTotal(workspace.projects, quality);
  const askRun = async(action) => {
    setActionError(null);
    run.clearReport();
    try {
      setPending({ action, plan: await run.prepare(action, workspace.projects) });
    } catch (failed) {
      setActionError(failed?.message || 'The projects could not be checked. Try again.');
    }
  };
  const confirmRun = async() => {
    const { action, plan } = pending;
    setPending(null);
    await run.execute(action, plan);
  };

  const mayRemove = (project) => canManage(workspace) || (user?.sub && project.owner?.sub === user.sub);
  const projects = (
    <WorkspaceProjects
      activity={activity}
      canEdit={canEdit(workspace)}
      mayRemove={mayRemove}
      onChanged={load}
      onOpenProject={openProject}
      onQualityReload={reloadQuality}
      projectRun={projectRun}
      quality={quality}
      run={run}
      runStates={projectRun.states}
      userSub={user?.sub}
      workspace={workspace}
    />
  );

  return (
    <div className="sbx-home">
      <div className="sbx-home-inner workspaces is-full-width" data-testid="workspace-page-main">
        <WorkspaceHeader
          busy={run.busy}
          canManage={canManage(workspace)}
          hasProjects={workspace.projects.length > 0}
          onBack={() => navigate('/workspaces')}
          onDelete={() => setConfirmDelete(true)}
          onInvite={() => setTab('members')}
          onRename={() => setRenaming(true)}
          onStart={() => askRun('start')}
          onStop={() => askRun('stop')}
          roleText={ROLE_TEXT[workspace.role]}
          workspace={workspace}
        />
        {workspace.description && <p className="workspaces-note">{workspace.description}</p>}
        {actionError && <p className="sbx-action-error" role="alert">{actionError}</p>}
        {run.report && (
          <p className={`ws-report is-${run.report.tone}`} data-testid="run-report" role="status">
            {run.report.text} <button className="ws-link" onClick={run.clearReport} type="button">Dismiss</button>
          </p>
        )}
        <Tabs
          activeKey={tab}
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <WorkspaceOverview
                  activity={activity}
                  onOpenProject={openProject}
                  onOpenTab={setTab}
                  quality={quality}
                  runStates={projectRun.states}
                  userSub={user?.sub}
                  workspace={workspace}
                />
              ),
            },
            { key: 'projects', label: <>Projects <span className="ws-count">{workspace.projects.length}</span></>, children: projects },
            {
              key: 'quality',
              label: <>Quality {blocked > 0 && <span aria-label={`${blocked} block Publish`} className="ws-count is-bad">{blocked}</span>}</>,
              children: (
                <WorkspaceQuality
                  onOpenProject={openProject}
                  onReload={() => { reloadQuality(); reloadSecrets(); }}
                  quality={quality}
                  runStates={projectRun.states}
                  secrets={secrets}
                  workspace={workspace}
                />
              ),
            },
            {
              key: 'activity',
              label: 'Activity',
              children: <WorkspaceActivity activity={activity} onOpenProject={openProject} onReload={reloadActivity} userSub={user?.sub} workspace={workspace} />,
            },
            { key: 'members', label: <>Members <span className="ws-count">{workspace.members.length + 1}</span></>, children: <WorkspaceMembers onChange={load} user={user} workspace={workspace} /> },
            { key: 'brain', label: 'Brain', children: <WorkspaceBrain workspace={workspace} /> },
            { key: 'settings', label: 'Settings', children: <WorkspaceSettings canManage={canManage(workspace)} onDelete={() => setConfirmDelete(true)} onRename={() => setRenaming(true)} workspace={workspace} /> },
          ]}
          onChange={setTab}
        />
      </div>
      <RunConfirm onCancel={() => setPending(null)} onConfirm={confirmRun} pending={pending} />
      <RenameWorkspace onClose={() => setRenaming(false)} onSaved={load} open={renaming} workspace={workspace} />
      <Modal
        okButtonProps={{ danger: true }}
        okText="Delete workspace"
        onCancel={() => setConfirmDelete(false)}
        onOk={remove}
        open={confirmDelete}
        title={`Delete “${workspace.name}”?`}
      >
        <p>Its projects are kept, with their owners and the people they are shared with directly. Its members lose the access the workspace gave them. Its brain becomes a project of yours.</p>
      </Modal>
    </div>
  );
}
